import { useEffect, useRef, useState, useCallback } from "react";
import type { PlayerStatus, PlayerHookReturn } from "../types";
import { SquawkV2WebSocket } from "./SquawkV2WebSocket";
import { diagnosticEventLog } from "../../../shared/services/diagnostic";

// TODO: Replace with Keycloak token integration (or backend token proxy).
// Currently a hardcoded squawk-stub listener token (roles: listen + all asset:* channels).
// Expires ~June 2027 (exp 1812725458).
const V2_AUTH_TOKEN = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiI4ZjE0OTYwMy04MTU1LTU1MGYtYmU3MS1kZjBmZWEyODc5MDIiLCJyb2xlcyI6WyJsaXN0ZW4iLCJhc3NldDptdWx0aV9hc3NldCIsImFzc2V0OmZvcmV4IiwiYXNzZXQ6ZW5lcmd5IiwiYXNzZXQ6ZXF1aXRpZXMiLCJhc3NldDpmaXhlZF9pbmNvbWUiLCJhc3NldDptZXRhbHMiXSwiZXhwIjoxODEyNzI1NDU4LCJpYXQiOjE3ODExODk0NTgsImlzcyI6InNxdWF3ay1zdHViIiwiZW1haWwiOiJ1c2VyQGV4YW1wbGUuY29tIn0.Lua6EtQp-kBZMZhiLNRkqkXk58OqAx3RKvcxmfHp5TM";

export const MAX_V2_RETRIES = 5;
const MAX_RETRIES = MAX_V2_RETRIES;
const BASE_RETRY_DELAY_MS = 1500;
const MAX_RETRY_DELAY_MS = 30000;

// Consecutive streams that fail to play (e.g. the backend sends WebM the
// browser can't demux) before we declare v2 audio unusable and let the
// player fallback chain take over (→ webrtc/Flussonic → the v1 squawk).
const PLAYBACK_FAILURE_THRESHOLD = 2;

interface PlaybackErrorInfo {
  code?: number;
  message?: string;
}

interface UseSquawkV2PlayerOptions {
  v2Keyword: string | null;
  enabled: boolean;
  authenticated: boolean;
}

function getRetryDelay(attempt: number): number {
  const exponential = BASE_RETRY_DELAY_MS * Math.pow(2, attempt);
  const capped = Math.min(exponential, MAX_RETRY_DELAY_MS);
  return Math.round(capped * (0.5 + Math.random() * 0.5));
}

function buildWsUrl(): string {
  const baseUrl = import.meta.env.VITE_AUDIO_V2_BASE_URL;
  if (!baseUrl) {
    console.error("[SquawkV2] VITE_AUDIO_V2_BASE_URL is not set");
    return "";
  }
  return `${baseUrl}/api/ws?token=${encodeURIComponent(V2_AUTH_TOKEN)}`;
}

/**
 * React hook for managing the v2 WebSocket + MSE audio player.
 *
 * Connects to the audio-v2 backend via WebSocket, subscribes to a keyword-based
 * channel, and plays streamed WebM/Opus audio through MediaSource Extensions.
 *
 * Returns the same { status, error, retryCount, setVolume } interface as
 * useWebRTCPlayer / useMSEPlayer / useShoutcastPlayer for drop-in compatibility.
 */
export const useSquawkV2Player = ({
  v2Keyword,
  enabled,
  authenticated,
}: UseSquawkV2PlayerOptions): PlayerHookReturn => {
  const [status, setStatus] = useState<PlayerStatus>("idle");
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const wsRef = useRef<SquawkV2WebSocket | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectionIdRef = useRef(0);
  // Count of consecutive streams that failed to play; reset when audio actually
  // plays. The WebSocket can be perfectly healthy ("connected") while every
  // stream is undecodable, so playback health is tracked separately here.
  const playbackFailuresRef = useRef(0);

  const setVolume = useCallback((volume: number) => {
    wsRef.current?.set_volume(volume);
  }, []);

  // Wire playback-health handlers onto a ws instance. Playback failures don't
  // come through the WebSocket "error" event (the socket is fine) — they arrive
  // as media-element errors. After PLAYBACK_FAILURE_THRESHOLD consecutive bad
  // streams we report status:"error" with retries exhausted, which the
  // SquawkPlayerContext auto-fallback effect reacts to by switching transport.
  const attachPlaybackHandlers = useCallback(
    (ws: SquawkV2WebSocket, thisConnectionId: number) => {
      ws.on("playing", () => {
        if (connectionIdRef.current !== thisConnectionId) return;
        playbackFailuresRef.current = 0;
      });

      ws.on("playback_error", (info: unknown) => {
        if (connectionIdRef.current !== thisConnectionId) return;
        const detail = (info && typeof info === "object" ? info : {}) as PlaybackErrorInfo;
        playbackFailuresRef.current += 1;
        console.warn(
          `[SquawkV2 #${thisConnectionId}] Playback error ` +
            `(${playbackFailuresRef.current}/${PLAYBACK_FAILURE_THRESHOLD}): ` +
            `code=${detail.code} ${detail.message ?? ""}`
        );
        diagnosticEventLog.record("squawk-v2:audio", "warn", "playback-error", {
          connectionId: thisConnectionId,
          failures: playbackFailuresRef.current,
          code: detail.code,
          message: detail.message,
        });

        if (playbackFailuresRef.current >= PLAYBACK_FAILURE_THRESHOLD) {
          console.error(
            `[SquawkV2 #${thisConnectionId}] Playback failing repeatedly — escalating to fallback chain`
          );
          diagnosticEventLog.record("squawk-v2:audio", "error", "playback-fallback", {
            connectionId: thisConnectionId,
            failures: playbackFailuresRef.current,
            lastMessage: detail.message,
          });
          // Report an exhausted error so SquawkPlayerContext falls to the next
          // transport (webrtc). retryCount=MAX also makes the retry effect give up.
          setStatus("error");
          setError(new Error(`v2 audio undecodable: ${detail.message ?? "playback failed"}`));
          setRetryCount(MAX_RETRIES);
        }
      });
    },
    []
  );

  // Cleanup function
  const cleanup = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.destroy();
      wsRef.current = null;
    }
  }, []);

  // Connection effect
  useEffect(() => {
    if (!enabled || !authenticated || !v2Keyword) {
      setStatus("idle");
      setError(null);
      setRetryCount(0);
      cleanup();
      return;
    }

    const wsUrl = buildWsUrl();
    if (!wsUrl) {
      setStatus("error");
      setError(new Error("VITE_AUDIO_V2_BASE_URL is not configured"));
      return;
    }

    // Reset for new connection
    cleanup();

    connectionIdRef.current++;
    const thisConnectionId = connectionIdRef.current;

    console.log(`[SquawkV2 #${thisConnectionId}] Connecting for keyword: ${v2Keyword}`);
    diagnosticEventLog.record("squawk-v2:audio", "info", "connection-start", {
      connectionId: thisConnectionId,
      keyword: v2Keyword,
    });

    setStatus("connecting");
    setError(null);
    setRetryCount(0);
    playbackFailuresRef.current = 0;

    // Create WebSocket client. Audio plays through a plain <audio> element
    // (no Web Audio AudioContext) — see SquawkV2WebSocket for why.
    const ws = new SquawkV2WebSocket(wsUrl);
    wsRef.current = ws;

    // Event handlers
    ws.on("connected", () => {
      if (connectionIdRef.current !== thisConnectionId) return;
      diagnosticEventLog.record("squawk-v2:audio", "info", "ws-connected", {
        connectionId: thisConnectionId,
      });
      // Subscribe to the channel keyword
      ws.subscribe([v2Keyword]);
    });

    ws.on("subscribed", () => {
      if (connectionIdRef.current !== thisConnectionId) return;
      console.log(`[SquawkV2 #${thisConnectionId}] Subscribed to ${v2Keyword}`);
      diagnosticEventLog.record("squawk-v2:audio", "info", "subscribed", {
        connectionId: thisConnectionId,
        keyword: v2Keyword,
      });
      setStatus("connected");
      setError(null);
    });

    ws.on("stream_start", () => {
      if (connectionIdRef.current !== thisConnectionId) return;
      console.log(`[SquawkV2 #${thisConnectionId}] Stream started`);
    });

    ws.on("error", (err: unknown) => {
      if (connectionIdRef.current !== thisConnectionId) return;
      const errMsg = typeof err === "string" ? err : "WebSocket error";
      console.error(`[SquawkV2 #${thisConnectionId}] Error:`, errMsg);
      diagnosticEventLog.record("squawk-v2:audio", "error", "error", {
        connectionId: thisConnectionId,
        error: errMsg,
      });
      setStatus("error");
      setError(new Error(errMsg));
    });

    ws.on("disconnected", () => {
      if (connectionIdRef.current !== thisConnectionId) return;
      console.log(`[SquawkV2 #${thisConnectionId}] Disconnected`);
      diagnosticEventLog.record("squawk-v2:audio", "info", "disconnected", {
        connectionId: thisConnectionId,
      });
      // Don't set status here — the SquawkV2WebSocket handles auto-reconnect
      // Status will be updated via reconnecting event or error
    });

    ws.on("reconnecting", (attempt: unknown) => {
      if (connectionIdRef.current !== thisConnectionId) return;
      console.log(`[SquawkV2 #${thisConnectionId}] Reconnecting (attempt ${attempt})`);
      setStatus("connecting");
      setRetryCount(typeof attempt === "number" ? attempt : 0);
    });

    attachPlaybackHandlers(ws, thisConnectionId);

    // Connect
    ws.connect();

    return () => {
      cleanup();
      setStatus("idle");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, authenticated, v2Keyword]);

  // Retry effect — handle errors that exceed the WebSocket's built-in reconnect
  useEffect(() => {
    if (status !== "error" || !error || !enabled || !v2Keyword) return;

    if (retryCount >= MAX_RETRIES) {
      console.error(`[SquawkV2] Max retries (${MAX_RETRIES}) reached, giving up`);
      diagnosticEventLog.record("squawk-v2:audio", "error", "max-retries-reached", {
        retryCount,
      });
      return;
    }

    const delay = getRetryDelay(retryCount);
    console.log(
      `[SquawkV2] Retrying in ${(delay / 1000).toFixed(1)}s (attempt ${retryCount + 1}/${MAX_RETRIES})`
    );

    retryTimeoutRef.current = setTimeout(() => {
      if (!enabled || !v2Keyword) return;

      setRetryCount((prev) => prev + 1);
      setStatus("connecting");
      setError(null);

      // Reconnect by triggering a new connection via cleanup + re-create
      cleanup();
      connectionIdRef.current++;
      const thisConnectionId = connectionIdRef.current;

      const wsUrl = buildWsUrl();
      if (!wsUrl) return;

      const ws = new SquawkV2WebSocket(wsUrl);
      wsRef.current = ws;

      ws.on("connected", () => {
        if (connectionIdRef.current !== thisConnectionId) return;
        ws.subscribe([v2Keyword]);
      });

      ws.on("subscribed", () => {
        if (connectionIdRef.current !== thisConnectionId) return;
        setStatus("connected");
        setError(null);
      });

      ws.on("error", (err: unknown) => {
        if (connectionIdRef.current !== thisConnectionId) return;
        setStatus("error");
        setError(new Error(typeof err === "string" ? err : "WebSocket error"));
      });

      ws.on("reconnecting", (attempt: unknown) => {
        if (connectionIdRef.current !== thisConnectionId) return;
        setStatus("connecting");
        setRetryCount(typeof attempt === "number" ? attempt : 0);
      });

      attachPlaybackHandlers(ws, thisConnectionId);

      ws.connect();
    }, delay);

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, error, retryCount, enabled, v2Keyword]);

  return {
    status,
    error,
    retryCount,
    setVolume,
  };
};
